/**
 * Quality Lab types.
 *
 * Every document in the lab carries three flags. STORE says which set it is
 * in: MEMORY (the examples the models learn from, it "teaches") or EVAL (the
 * held-out set they are scored against, it "tests"). The two never overlap and
 * a document never moves between them. STATE is how well its extraction is
 * trusted, rising from unverified to expert verified. SOURCE says whether it
 * arrived automatically or was added by hand.
 *
 * Whether a document is in use is not a fourth flag. It follows from STATE, and
 * the only thing a person sets is whether the document has been REMOVED.
 */

export enum QualityLabStore {
  Memory = "MEMORY",
  Eval = "EVAL",
}

export enum QualityLabState {
  Unverified = "UNVERIFIED",
  Verified = "VERIFIED",
  Expert = "EXPERT",
}

export enum QualityLabSource {
  Auto = "AUTO",
  Human = "HUMAN",
}

/**
 * Whether the models use a document. Not stored and not chosen: an unverified
 * document is one nobody has checked, so neither store rests on it, and
 * verifying is what brings it into use. Deriving this is what keeps it from ever
 * disagreeing with the state it is derived from.
 */
export const isActive = (doc: { state: QualityLabState; removed: boolean }) =>
  !doc.removed && doc.state !== QualityLabState.Unverified;

/**
 * Whether a document set is already in a store. Served to anyone who can open
 * the document, not only Quality Lab admins, so the document detail screen can
 * choose between offering "add to Memory" and reporting it is already in.
 */
export interface Membership {
  member: boolean;
  store: QualityLabStore | null;
  state: QualityLabState | null;
  source: QualityLabSource | null;
  removed: boolean;
  addedBy: string | null;
  addedAt: string | null;
  /** Approved or exported, and not already in a store. */
  eligible: boolean;
}

/** URL tab keys. Spelled out: a route is read by people, and "mem" is not a word. */
export type SetKey = "memory" | "evaluation";

/** One document inside Memory or Evaluation. The store is implied by the tab. */
export interface QualityLabDoc {
  id: string;
  title: string;
  formType: string;
  /** The counterparty it is categorized against. Null when it is not categorized. */
  party: string | null;
  state: QualityLabState;
  source: QualityLabSource;
  addedBy: string;
  date: string;
  /** Who vouched for this document, and when. Null while nobody has. */
  verifiedBy: string | null;
  verifiedAt: string | null;
  documentId?: string;
  /** Taken out of the store by hand. Only listed when asked for. */
  removed: boolean;
  /** Who took it out, and when. Null while it is still in the store. */
  removedBy: string | null;
  removedAt: string | null;
}

/**
 * A page of a set browser. `total` counts what the active filters match and so
 * drives the paging; the four counts below it describe the WHOLE store and
 * ignore the filters, because the stats strip says what the set is made of, not
 * what is currently on screen. They add up to storeTotal, never to total.
 */
export interface QualityLabPage {
  content: Array<QualityLabDoc>;
  total: number;
  storeTotal: number;
  expert: number;
  verified: number;
  unverified: number;
  page: number;
  pages: number;
  /**
   * What to head the counterparty column with, named by whichever categorization
   * fills it. Null when the setup has no counterparty, and the column is then
   * left out rather than headed with a name for something it does not hold.
   */
  partyLabel: string | null;
}

export interface QualityLabPageArgs {
  set: SetKey;
  search?: string;
  formType?: string;
  state?: QualityLabState;
  source?: QualityLabSource;
  /** False for the store, true for what has been taken out of it. */
  removed?: boolean;
  sort?: "recent" | "title" | "state";
  page?: number;
  size?: number;
}

/** A document on the verification worklist, which spans both stores. */
export interface Candidate {
  id: string;
  title: string;
  formType: string;
  party: string | null;
  store: QualityLabStore;
  state: QualityLabState;
  source: QualityLabSource;
  /** Who put it in the store, for a document somebody added by hand. */
  addedBy?: string | null;
  documentId?: string;
}

/**
 * The verification worklist: a batch to work through, plus what is behind it.
 * The seed is echoed back so the same draw can be asked for again; a different
 * one redraws.
 */
export interface CandidateBatch {
  batch: Array<Candidate>;
  waiting: number;
  seed: string;
}

export interface CandidateDetail extends Candidate {
  addedBy: string;
  date: string;
  /**
   * Who vouched for this document, and when. Null until someone has. A verified
   * document without a name behind it is a claim nobody can be asked about.
   */
  verifiedBy: string | null;
  verifiedAt: string | null;
  /**
   * How many times the labels have been re-annotated since export. 0 means they
   * are as exported. Anything higher means the extraction below is not the one
   * the document left the flow with. The verification survives that, because
   * only an expert can re-annotate.
   */
  annotationVersion: number;
  removed: boolean;
  removedBy: string | null;
  removedAt: string | null;
}

/** A processed document that could be pulled into a set by hand. */
export interface LookupDoc {
  id: string;
  title: string;
  formType: string;
  party: string | null;
  approvedDate: string;
  state: QualityLabState;
  documentId?: string;
}

export interface LookupPage {
  content: Array<LookupDoc>;
  total: number;
  page: number;
  pages: number;
  /** @see QualityLabPage.partyLabel */
  partyLabel: string | null;
}

/**
 * What an add actually did. Each document is added on its own, so a rejection costs
 * only itself; the caller has to be told which ones those were, or a curator walks
 * away believing a document is in the store when it is not.
 */
export interface AddToSetResult {
  added: Array<string>;
  rejected: Array<{ documentSetId: string; reason: string }>;
}

/**
 * Takes documents out of a store, or puts them back. No store key: the endpoint
 * keys off the document, and a document never moves between stores, so naming
 * one here could only ever be a guess the caller does not need to make.
 */
export interface SetRemovedArgs {
  documentSetIds: Array<string>;
  removed: boolean;
}

/** One key the search box understands, with what it filters on. */
export interface SearchFacet {
  key: string;
  hint: string;
}
